<template>
  <div class="pagination">
    <button
      class="pagination-btn"
      :disabled="currentPage === 1"
      @click="onPageChange(currentPage - 1)"
    >
      <img src="@/assets/img/arrow-left.png" alt="" />
    </button>

    <div class="page-numbers">
      <template v-for="page in visiblePages" :key="page">
        <button
          v-if="page !== '...'"
          class="page-number"
          :class="{ active: page === currentPage }"
          @click="onPageChange(page)"
          :disabled="page === currentPage"
        >
          {{ page }}
        </button>
        <span v-else class="ellipsis">{{ page }}</span>
      </template>
    </div>

    <button
      class="pagination-btn"
      :disabled="currentPage === totalPages"
      @click="onPageChange(currentPage + 1)"
    >
      <img src="@/assets/img/arrow-right.png" alt="" />
    </button>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'

const props = defineProps({
  currentPage: {
    type: Number,
    required: true
  },
  totalPages: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['page-change'])

// Calculate visible page numbers with ellipsis
const visiblePages = computed(() => {
  const delta = 1 // Number of pages to show before and after current page
  let pages = []

  if (props.totalPages <= 3) {
    // Show all pages if total is 7 or less
    pages = Array.from({ length: props.totalPages }, (_, i) => i + 1)
  } else {
    // Always include first page
    pages.push(1)

    const leftBound = Math.max(2, props.currentPage - delta)
    const rightBound = Math.min(props.totalPages - 1, props.currentPage + delta)

    // Add visible pages
    for (let i = leftBound; i <= rightBound; i++) {
      pages.push(i)
    }

    // Always include last page
    pages.push(props.totalPages)
  }

  return pages
})

const onPageChange = page => {
  if (page >= 1 && page <= props.totalPages) {
    emit('page-change', page)
  }
}
</script>

<style scoped>
.pagination {
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 1rem;
  margin: 2rem 0;
}

.page-numbers {
  display: flex;
  gap: 0.5rem;
}

.page-number {
  font-family: 'figtree-medium', sans-serif;
  padding: 0.45rem 0.7rem;
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 0.8rem;
}

.pagination-btn {
  width: 15px;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
}

.pagination-btn img {
  display: block;
}

.page-number:hover:not(.active):not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}

.pagination-btn:disabled {
  opacity: 0.5;
}

.page-number.active {
  background: #0f52ba;
  border-color: #0f52ba;
  color: white;
  cursor: default;
}

.ellipsis {
  color: #fff;
  padding: 0.5rem;
  user-select: none;
}

@media (max-width: 768px) {
  .pagination {
    justify-content: center;
  }

  .pagination {
    scale: 0.8;
  }
}
</style>
